import React, { useState } from 'react';
import { Mail, MessageSquare, Phone, Share2, Megaphone, Search, Users, AtSign, Globe, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Features = () => {
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const features = [
    {
      title: 'Email Marketing',
      description: 'AI-powered email campaigns that deliver personalized content to each recipient, increasing engagement and conversions.',
      imageUrl: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <Mail className="h-6 w-6" />,
      color: 'blue',
      id: 'email-marketing'
    },
    {
      title: 'SMS Marketing',
      description: 'Direct and instant communication with your customers through SMS campaigns with high open rates.',
      imageUrl: 'https://images.pexels.com/photos/33999/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <MessageSquare className="h-6 w-6" />,
      color: 'green',
      id: 'sms-marketing'
    },
    {
      title: 'WhatsApp Marketing',
      description: 'Engage customers through WhatsApp Business API with rich media messages and automated responses.',
      imageUrl: 'https://images.pexels.com/photos/5053740/pexels-photo-5053740.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <Phone className="h-6 w-6" />,
      color: 'emerald',
      id: 'whatsapp-marketing'
    },
    {
      title: 'Social Media Marketing',
      description: 'Comprehensive social media management across all major platforms with analytics and scheduling.',
      imageUrl: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <Share2 className="h-6 w-6" />,
      color: 'indigo',
      id: 'social-media'
    },
    {
      title: 'Online Advertising',
      description: 'Strategic digital advertising campaigns across multiple platforms to reach your target audience.',
      imageUrl: 'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <Megaphone className="h-6 w-6" />,
      color: 'purple',
      id: 'advertising'
    },
    {
      title: 'SEO Services',
      description: 'Improve your search engine rankings with our comprehensive SEO strategies and tools.',
      imageUrl: 'https://images.pexels.com/photos/196646/pexels-photo-196646.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <Search className="h-6 w-6" />,
      color: 'amber',
      id: 'seo'
    },
    {
      title: 'KOL Marketing',
      description: 'Connect with influential voices in your industry to amplify your brand message.',
      imageUrl: 'https://images.pexels.com/photos/2422294/pexels-photo-2422294.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <Users className="h-6 w-6" />,
      color: 'pink',
      id: 'kol'
    },
    {
      title: 'Business Email',
      description: 'Professional email hosting with advanced security and collaboration features.',
      imageUrl: 'https://images.pexels.com/photos/4348404/pexels-photo-4348404.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <AtSign className="h-6 w-6" />,
      color: 'cyan',
      id: 'business-email'
    },
    {
      title: 'Website Builder',
      description: 'Create stunning websites with our easy-to-use drag-and-drop website builder.',
      imageUrl: 'https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <Globe className="h-6 w-6" />,
      color: 'teal',
      id: 'website-builder'
    },
    {
      title: 'CRM System',
      description: 'Manage customer relationships effectively with our comprehensive CRM solution.',
      imageUrl: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: <UserCheck className="h-6 w-6" />,
      color: 'rose',
      id: 'crm'
    }
  ];

  const getColorClasses = (color: string) => ({
    icon: `text-${color}-600`,
    bg: `bg-${color}-50`,
    border: `border-${color}-200`,
    hover: `hover:border-${color}-400`,
    shadow: `group-hover:shadow-${color}-100`
  });

  const handleServiceClick = (serviceId: string) => {
    navigate('/pricing', { state: { selectedService: serviceId } });
  };

  return (
    <div className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Comprehensive Digital Marketing Solutions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to grow your business online with our integrated marketing and business tools.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const colors = getColorClasses(feature.color);
            return (
              <div
                key={index}
                className={`group relative cursor-pointer transition-all duration-300 transform hover:-translate-y-1
                  ${hoveredIndex !== null && hoveredIndex !== index ? 'opacity-50' : 'opacity-100'}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => handleServiceClick(feature.id)}
              >
                <div className={`relative overflow-hidden rounded-xl border ${colors.border} ${colors.hover} 
                  transition-all duration-300 ${colors.bg} group-hover:shadow-xl ${colors.shadow}`}>
                  <div className="p-6">
                    <div className={`${colors.icon} mb-4`}>
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/10 opacity-0 
                    group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Features;